import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Divider, Button, CircularProgress,
  Switch, FormControlLabel, Alert, Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import EmpSelectbox from '../common/form/autocomplete/EmpSelectbox';
import * as manufacturingApi from '../../lib/api/manufacturing';

/**
 * 생산 설정 — 기본 담당자와 기능 사용 여부.
 *
 * 발주 단계에서 원가 내역으로 생산계획을 자동으로 만들 때 설계·생산·QC 담당자가 필요한데,
 * 원가 내역에는 담당자가 없다. 여기서 미리 정해두면 그 값으로 채워진다.
 *
 * 옆 탭들과 달리 사람마다 정하는 것이 아니다. <b>회사에 하나씩</b>인 값이라
 * 왼쪽 사원 목록이 없고 칸 세 개만 있다.
 *
 * 비워두어도 된다. 그러면 계획을 만들 때 담당자 칸이 빈 채로 열리고 그 자리에서 고른다.
 */
const ManufacturingSettingPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const formik = useFormik({
    initialValues: {
      enabled: false,
      designUuid: '',
      designName: '',
      productionUuid: '',
      productionName: '',
      qcUuid: '',
      qcName: '',
    },
    enableReinitialize: false,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const res = await manufacturingApi.saveManufacturingSettings({
          enabled: values.enabled,
          designUuid: values.designUuid || '',
          productionUuid: values.productionUuid || '',
          qcUuid: values.qcUuid || '',
        });
        if (res.data?.success) {
          // 서버가 저장한 결과를 그대로 돌려준다. 그것으로 화면을 맞춰두면
          // 저장은 됐는데 화면만 다른 상황이 생기지 않는다.
          apply(res.data.data);
          enqueueSnackbar('생산 설정을 저장했습니다.', { variant: 'success' });
        } else {
          enqueueSnackbar(res.data?.message || '저장하지 못했습니다.', { variant: 'error' });
        }
      } catch (err) {
        enqueueSnackbar(err?.response?.data?.message || '저장하지 못했습니다.', { variant: 'error' });
      } finally {
        setSaving(false);
      }
    },
  });

  const apply = (data) => {
    formik.setValues({
      enabled: Boolean(data?.enabled),
      designUuid: data?.designUuid || '',
      designName: data?.designName || '',
      productionUuid: data?.productionUuid || '',
      productionName: data?.productionName || '',
      qcUuid: data?.qcUuid || '',
      qcName: data?.qcName || '',
    });
  };

  useEffect(() => {
    let alive = true;
    manufacturingApi.getManufacturingSettings()
      .then((res) => {
        if (!alive) return;
        if (res.data?.success) apply(res.data.data);
      })
      .catch(() => {
        if (alive) enqueueSnackbar('생산 설정을 불러오지 못했습니다.', { variant: 'error' });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const managers = [
    { uuid: 'designUuid', name: 'designName', label: '기본 설계 담당자' },
    { uuid: 'productionUuid', name: 'productionName', label: '기본 생산 담당자' },
    { uuid: 'qcUuid', name: 'qcName', label: '기본 QC 담당자' },
  ];

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          생산계획을 만들 때 담당자 칸에 미리 채워둘 사람입니다. 회사 전체에 하나씩 적용됩니다.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          onClick={formik.handleSubmit}
          disabled={saving}
        >
          저장
        </Button>
      </Box>

      <Paper elevation={1} sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          기본 담당자
        </Typography>
        <Typography variant="caption" color="text.secondary">
          비워두면 생산계획을 만들 때 빈 칸으로 열립니다. 그 자리에서 고르셔도 됩니다.
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {managers.map((m) => (
            <Grid item xs={12} md={4} key={m.uuid}>
              <EmpSelectbox
                formik={formik}
                name={m.uuid}
                nameField={m.name}
                label={m.label}
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          생산 기능
        </Typography>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              checked={formik.values.enabled}
              onChange={(e) => formik.setFieldValue('enabled', e.target.checked)}
            />
          }
          label={formik.values.enabled ? '사용' : '사용 안 함'}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          끄면 수주품의서를 발주 단계로 넘길 때 생산계획 자동 생성 안내가 뜨지 않습니다.
          이미 만들어진 생산계획은 그대로 남습니다.
        </Typography>

        {!formik.values.enabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            지금은 생산 기능을 쓰지 않는 상태입니다. 기본 담당자를 정해두어도 쓰이지 않습니다.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default ManufacturingSettingPanel;
